"use client";

import React, { useState, useCallback, useRef } from "react";
import QuillEditor from "@/components/product/QuillEditor";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Braces, Plus } from "lucide-react";
import Quill from "quill";

interface Variable {
  id: string;
  label: string;
  placeholder: string;
  description?: string;
}

interface RichTextWithVariablesProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  label?: string;
  error?: string;
  required?: boolean;
}

const AVAILABLE_VARIABLES: Variable[] = [
  {
    id: "customer_name",
    label: "Customer Name",
    placeholder: "{{customer_name}}",
    description: "Customer's first and last name"
  },
  {
    id: "customer_first_name",
    label: "Customer First Name", 
    placeholder: "{{customer_first_name}}",
    description: "Customer's first name only"
  },
  {
    id: "order_number",
    label: "Order Number",
    placeholder: "{{order_number}}",
    description: "Unique order identifier"
  },
  {
    id: "order_total",
    label: "Order Total",
    placeholder: "{{order_total}}",
    description: "Total order amount"
  },
  {
    id: "product_name",
    label: "Product Name",
    placeholder: "{{product_name}}",
    description: "Name of the purchased product"
  },
  {
    id: "store_name",
    label: "Store Name",
    placeholder: "{{store_name}}",
    description: "Your store's name"
  },
  {
    id: "cart_total",
    label: "Cart Total",
    placeholder: "{{cart_total}}",
    description: "Total abandoned cart amount"
  },
  {
    id: "cart_items",
    label: "Cart Items",
    placeholder: "{{cart_items}}",
    description: "List of items in abandoned cart"
  }
];

export const RichTextWithVariables: React.FC<RichTextWithVariablesProps> = ({
  value,
  onChange,
  placeholder = "Write your message...",
  height = 150,
  label,
  error
  ,required = false
}) => {
  const [content, setContent] = useState(value);
  const quillInstanceRef = useRef<Quill | null>(null);

  const handleEditorReady = useCallback((quill: Quill) => {
    quillInstanceRef.current = quill;
  }, []);

  const handleContentChange = useCallback((newContent: string) => {
    // QuillEditor emits a Delta-like object; we store it as a JSON string
    // so it can round-trip across clients and backend consistently.
    const contentString =
      typeof newContent === "string"
        ? newContent
        : newContent
          ? JSON.stringify(newContent)
          : "";
    setContent(contentString);
    onChange(contentString);
  }, [onChange]);

  const insertVariable = useCallback((variable: Variable) => {
    const quill = quillInstanceRef.current;
    if (!quill) return;

    // Get current selection/cursor position
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();

    // Insert the variable chip as a custom embed (truly non-editable)
    quill.insertEmbed(index, 'variableChip', {
      label: variable.label,
      placeholder: variable.placeholder,
    }, 'user');
    
    // Add a space after the variable chip
    quill.insertText(index + 1, ' ', 'user');
    
    // Move cursor after the inserted chip and space
    quill.setSelection(index + 2, 0);
    
    // Focus the editor
    quill.focus();
  }, []);

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-[13px] font-normal text-muted-foreground">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        {/* Variables Toolbar */}
        <div className="flex items-center justify-between p-2 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Braces className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">Variables</span>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 px-2">
                <Plus className="h-3 w-3 mr-1" />
                Insert Variable
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {AVAILABLE_VARIABLES.map((variable) => (
                <DropdownMenuItem
                  key={variable.id}
                  onClick={() => insertVariable(variable)}
                  className="flex-col items-start"
                >
                  <div className="font-medium">{variable.label}</div>
                  <div className="text-xs text-muted-foreground">{variable.placeholder}</div>
                  {variable.description && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {variable.description}
                    </div>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Rich Text Editor */}
        <div className="relative">
          <QuillEditor
            initialValue={content}
            onChange={handleContentChange}
            onEditorReady={handleEditorReady}
            placeholder={placeholder}
            height={height}
          />
        </div>
      </div>

      {/* Variable Preview */}
      {content && typeof content === 'string' && content.includes("{{") && (
        <div className="p-3 bg-muted/50 rounded-lg border border-border">
          <div className="text-xs font-medium text-muted-foreground mb-2">Preview Variables:</div>
          <div className="flex flex-wrap gap-1">
            {AVAILABLE_VARIABLES.filter(variable => 
              typeof content === 'string' && content.includes(variable.placeholder)
            ).map((variable) => (
              <span 
                key={variable.id}
                className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
              >
                {variable.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
};

export default RichTextWithVariables;
